import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, BarChart3, Clock, Calendar, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";


const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const SERVICE_COLORS = {
  cargo: "#059669", courier: "#2563eb", towing: "#dc2626",
  movers: "#7c3aed", roadside: "#ea580c", helpers: "#0891b2",
  nanny: "#d946ef", cleaning: "#65a30d", chef: "#ca8a04",
};

const DemandHeatmapPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState("all");
  const [viewState, setViewState] = useState({ latitude: 43.6532, longitude: -79.3832, zoom: 11.5 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/professional/demand-heatmap`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to load heatmap data"));
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const filteredPoints = data?.points?.filter(
    (p) => selectedService === "all" || p.service === selectedService
  ) || [];

  const geojson = {
    type: "FeatureCollection",
    features: filteredPoints.map((p) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { weight: p.weight, service: p.service },
    })),
  };

  const heatmapLayer = {
    id: "demand-heatmap",
    type: "heatmap",
    paint: {
      "heatmap-weight": ["get", "weight"],
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, 14, 3],
      "heatmap-color": [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(0,0,0,0)",
        0.2, "rgba(16,185,129,0.3)",
        0.4, "rgba(34,197,94,0.5)",
        0.6, "rgba(250,204,21,0.7)",
        0.8, "rgba(249,115,22,0.85)",
        1, "rgba(239,68,68,1)",
      ],
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 15, 14, 40],
      "heatmap-opacity": 0.8,
    },
  };

  const services = ["all", ...Object.keys(SERVICE_COLORS)];
  const peakHour = data?.hourly_distribution
    ? Object.entries(data.hourly_distribution).sort((a, b) => b[1] - a[1])[0]
    : null;
  const peakDay = data?.day_distribution
    ? Object.entries(data.day_distribution).sort((a, b) => b[1] - a[1])[0]
    : null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="demand-heatmap-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-5xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2">
                <Flame className="w-6 h-6 text-orange-500" /> Demand Heatmap
              </h1>
              <p className="text-sm text-stone-500 mt-1">See where demand is highest in your area</p>
            </div>
            <Badge className="bg-orange-100 text-orange-700" data-testid="total-bookings">
              {data?.total_bookings || 0} bookings tracked
            </Badge>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center" data-testid="peak-hour-stat">
              <Clock className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-emerald-950">{peakHour ? `${peakHour[0]}:00` : "--"}</p>
              <p className="text-xs text-stone-500">Peak Hour</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center" data-testid="peak-day-stat">
              <Calendar className="w-5 h-5 text-violet-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-emerald-950">{peakDay ? peakDay[0] : "--"}</p>
              <p className="text-xs text-stone-500">Busiest Day</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-stone-100 text-center" data-testid="hotspots-stat">
              <Layers className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-emerald-950">{filteredPoints.length}</p>
              <p className="text-xs text-stone-500">Hotspots</p>
            </div>
          </div>

          {/* Service Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide" data-testid="service-filters">
            {services.map((svc) => (
              <Button
                key={svc}
                size="sm"
                variant={selectedService === svc ? "default" : "outline"}
                className={`rounded-full capitalize text-xs shrink-0 ${selectedService === svc ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                onClick={() => setSelectedService(svc)}
                data-testid={`filter-${svc}`}
              >
                {svc === "all" ? "All Services" : svc}
              </Button>
            ))}
          </div>

          {/* Heatmap */}
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden mb-6" data-testid="heatmap-container">
            {loading ? (
              <div className="h-[400px] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div style={{ height: "400px" }}>
                <Map
                  {...viewState}
                  onMove={(e) => setViewState(e.viewState)}
                  mapStyle="mapbox://styles/mapbox/dark-v11"
                  mapboxAccessToken={MAPBOX_TOKEN}
                  style={{ width: "100%", height: "100%" }}
                >
                  <NavigationControl position="top-right" showCompass={false} />
                  <Source type="geojson" data={geojson}>
                    <Layer {...heatmapLayer} />
                  </Source>
                </Map>
              </div>
            )}
          </div>

          {/* Hourly Distribution Chart */}
          {data?.hourly_distribution && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-4" data-testid="hourly-chart">
              <h3 className="font-semibold text-emerald-950 flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-500" /> Hourly Demand Distribution
              </h3>
              <div className="flex items-end gap-1 h-32">
                {Object.entries(data.hourly_distribution).map(([hour, count]) => {
                  const maxCount = Math.max(...Object.values(data.hourly_distribution), 1);
                  const heightPct = (count / maxCount) * 100;
                  return (
                    <div key={hour} className="flex-1 flex flex-col items-center gap-1" title={`${hour}:00 - ${count} bookings`}>
                      <div
                        className="w-full rounded-t transition-all"
                        style={{
                          height: `${Math.max(heightPct, 4)}%`,
                          backgroundColor: heightPct > 70 ? "#ef4444" : heightPct > 40 ? "#f59e0b" : "#10b981",
                        }}
                      />
                      {parseInt(hour) % 3 === 0 && (
                        <span className="text-[9px] text-stone-400">{hour}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-stone-400">12 AM</span>
                <span className="text-[10px] text-stone-400">11 PM</span>
              </div>
            </div>
          )}

          {/* Service Breakdown */}
          {data?.service_counts && Object.keys(data.service_counts).length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="service-breakdown">
              <h3 className="font-semibold text-emerald-950 mb-3">Service Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(data.service_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([svc, count]) => {
                    const maxCount = Math.max(...Object.values(data.service_counts), 1);
                    return (
                      <div key={svc} className="flex items-center gap-3">
                        <span className="text-xs capitalize text-stone-600 w-20 truncate">{svc}</span>
                        <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${(count / maxCount) * 100}%`,
                              backgroundColor: SERVICE_COLORS[svc] || "#6b7280",
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium text-stone-700 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DemandHeatmapPage;
